
import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import { toast } from "sonner";

interface InventorySearchBoxProps {
  onSearch: (term: string) => void;
  onAiSearch: (term: string) => void;
}

const InventorySearchBox = ({ onSearch, onAiSearch }: InventorySearchBoxProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isAiSearching, setIsAiSearching] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  
  // We'll receive suggestions from the parent via props in a real implementation
  // This is just to maintain the same behavior
  useEffect(() => {
    if (debouncedSearchTerm.length > 0) {
      // Mock suggestions based on search term
      const mockSuggestions = [
        "Product A", "Product B", "Product C", "Product D",
        "Super Premium Verf", "Sigma Muurverf Wit"
      ].filter(item => 
        item.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      ).slice(0, 5);
      
      setSuggestions(mockSuggestions);
      setShowSuggestions(mockSuggestions.length > 0);
      onSearch(debouncedSearchTerm);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      onSearch("");
    }
  }, [debouncedSearchTerm, onSearch]);

  const handleSelectSuggestion = (suggestion: string) => {
    setSearchTerm(suggestion);
    setShowSuggestions(false);
    onSearch(suggestion);
  };
  
  const handleAiSearch = () => {
    if (!searchTerm.trim()) {
      toast.error("Voer een zoekterm in voor AI-zoeken");
      return;
    }
    
    setIsAiSearching(true);
    
    // Call the AI search function from props
    onAiSearch(searchTerm);
    
    // This will be handled by the parent component in a real implementation
    setTimeout(() => {
      setIsAiSearching(false);
    }, 1500);
  };

  return (
    <div className="mb-6">
      <p className="mb-2">Zoek producten in de voorraad:</p>
      <div className="flex space-x-2 relative">
        <div className="flex-1 relative">
          <Input
            placeholder="Zoek producten..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
          
          {showSuggestions && (
            <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-auto">
              {suggestions.map((suggestion, idx) => (
                <div
                  key={idx}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleSelectSuggestion(suggestion)}
                >
                  {suggestion}
                </div>
              ))}
            </div>
          )}
        </div>
        <Button onClick={handleAiSearch} disabled={isAiSearching} className="flex items-center gap-2">
          {isAiSearching ? (
            <>Zoeken...</>
          ) : (
            <>
              <Search className="h-4 w-4" />
              AI Zoeken
            </>
          )}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        Probeer bijvoorbeeld "verf", "tekort", of een specifieke productnaam
      </p>
    </div>
  );
};

export default InventorySearchBox;
